import {
  UnifiedLiquidityIndicator as KyokoUnifiedLiquidityIndicator,
  Tooltip,
  useMarketsContext,
  useSecuritiesContext,
  Z_INDEX,
  type UnifiedLiquidityToken,
} from '@talos/kyoko';
import { useMemo } from 'react';
import { UnifiedLiquidityMarketTooltipContent } from './UnifiedLiquidityMarketTooltipContent';
import {
  getUnifiedLiquiditySymbols,
  getUnifiedLiquidityTooltipGroupsFromTokens,
  type UnifiedLiquidityMarketTooltipGroup,
} from './unifiedLiquidityMarketTooltip';

interface UnifiedLiquidityTooltipIndicatorProps {
  groups?: UnifiedLiquidityMarketTooltipGroup[];
  baseSymbol?: string;
  tokens?: UnifiedLiquidityToken[];
  dataTestId: string;
  statusTestIdPrefix: string;
  showStatus?: boolean;
  zIndex?: number;
  isSmall?: boolean;
  interactive?: boolean;
  closeOnClickOutside?: boolean;
}

export function UnifiedLiquidityTooltipIndicator({
  groups,
  baseSymbol,
  tokens,
  dataTestId,
  statusTestIdPrefix,
  showStatus,
  zIndex = Z_INDEX.dropdown + 1,
  isSmall,
  interactive,
  closeOnClickOutside,
}: UnifiedLiquidityTooltipIndicatorProps) {
  const { securitiesBySymbol } = useSecuritiesContext();
  const { marketDisplayNameByName } = useMarketsContext();
  const tooltipGroups = useMemo(
    () =>
      groups ?? getUnifiedLiquidityTooltipGroupsFromTokens(tokens ?? [], securitiesBySymbol, marketDisplayNameByName),
    [groups, marketDisplayNameByName, securitiesBySymbol, tokens]
  );
  const unifiedEnabledSymbols = useMemo(
    () => getUnifiedLiquiditySymbols(tooltipGroups, { baseSymbol, enabledOnly: true }),
    [baseSymbol, tooltipGroups]
  );
  const isMultiProduct = getIsMultiProductUnifiedLiquidity(unifiedEnabledSymbols, securitiesBySymbol);
  const hasTooltip = tooltipGroups.some(group => group.rows.length > 0);

  return (
    <Tooltip
      usePortal
      placement="top-start"
      disabled={!hasTooltip}
      isSmall={isSmall}
      interactive={interactive}
      closeOnClickOutside={closeOnClickOutside}
      targetStyle={{ display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle' }}
      tooltip={
        <UnifiedLiquidityMarketTooltipContent
          groups={tooltipGroups}
          dataTestId={dataTestId}
          statusTestIdPrefix={statusTestIdPrefix}
          showStatus={showStatus}
        />
      }
      zIndex={zIndex}
    >
      <span>
        <KyokoUnifiedLiquidityIndicator isMultiProduct={isMultiProduct} />
      </span>
    </Tooltip>
  );
}

export function getIsMultiProductUnifiedLiquidity(
  symbols: string[],
  securitiesBySymbol: ReadonlyMap<string, { ProductType?: string }>
) {
  return new Set(symbols.map(symbol => securitiesBySymbol.get(symbol)?.ProductType).filter(Boolean)).size > 1;
}
